import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, Music as MusicIcon } from 'lucide-react';
import MusicPlayer from './MusicPlayer';
import { artistsService } from '../../services/api-service';
import './EventCard.css';

// Reusable Event Card - References central event and artist data
const EventCard = ({ event, showArtists = true, compact = false }) => {
  if (!event) return null;

  // Get full artist profiles from IDs (Single Source of Truth)
  // Note: Artists API not yet implemented, so this will return empty array
  const artists = event.artists ? artistsService.getArtistsByIds(event.artists) : [];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = () => {
    if (event.status === 'completed') {
      return <span className="event-card-badge event-card-badge-completed">Completed</span>;
    }
    if (event.status === 'upcoming') {
      return <span className="event-card-badge event-card-badge-upcoming">Upcoming</span>;
    }
    return null;
  };

  if (compact) {
    return (
      <Link to={`/events/${event.id}`} className="event-card event-card-compact">
        {event.flyer && (
          <div className="event-card-image-compact">
            <img src={event.flyer} alt={event.title} />
          </div>
        )}
        <div className="event-card-info-compact">
          <h4 className="event-card-title">{event.title}</h4>
          <p className="event-card-date">
            <Calendar size={16} />
            {formatDate(event.date)}
          </p>
        </div>
      </Link>
    );
  }

  return (
    <div className="event-card">
      {getStatusBadge()}

      {event.flyer && (
        <Link to={`/events/${event.id}`} className="event-card-image-link">
          <div className="event-card-image">
            <img src={event.flyer} alt={event.title} />
            <div className="event-card-overlay">
              <span className="event-card-overlay-text">View Details</span>
            </div>
          </div>
        </Link>
      )}

      <div className="event-card-content">
        <Link to={`/events/${event.id}`}>
          <h3 className="event-card-title">{event.title}</h3>
        </Link>

        <div className="event-card-meta">
          <div className="event-card-meta-item">
            <Calendar size={18} />
            <span>{formatDate(event.date)}</span>
          </div>

          {event.location && (
            <div className="event-card-meta-item">
              <MapPin size={18} />
              <span>{typeof event.location === 'string' ? event.location : (event.location.venue || event.location.address)}</span>
            </div>
          )}

          {event.time && (
            <div className="event-card-meta-item">
              <span className="event-card-time">
                {event.time.start} - {event.time.end}
              </span>
            </div>
          )}

          {(event.attendees || event.expectedAttendees) && (
            <div className="event-card-meta-item">
              <Users size={18} />
              <span>{event.attendees || event.expectedAttendees} people</span>
            </div>
          )}
        </div>

        {event.theme && (
          <div className="event-card-theme">
            <strong>Theme:</strong> {event.theme}
          </div>
        )}

        <p className="event-card-description">{event.description}</p>

        {event.tags && event.tags.length > 0 && (
          <div className="event-card-tags">
            {event.tags.map((tag, index) => (
              <span key={index} className="event-card-tag">
                #{tag}
              </span>
            ))}
          </div>
        )}

        {showArtists && artists.length > 0 && (
          <div className="event-card-artists">
            <div className="event-card-artists-header">
              <MusicIcon size={18} />
              <span>Artists</span>
            </div>
            <div className="event-card-artists-list">
              {artists.map((artist) => (
                <Link 
                  key={artist.id}
                  to={`/artists/${artist.id}`}
                  className="event-card-artist"
                >
                  <img src={artist.image} alt={artist.name} />
                  <span>{artist.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {event.recording && (
          <div className="event-card-recording">
            <MusicPlayer 
              track={event.recording}
              artistName="Various Artists"
            />
          </div>
        )}

        {event.streamRecordings && event.streamRecordings.length > 0 && (
          <div className="event-card-recordings">
            <div className="event-card-recordings-header">
              <span>📹 Previous Stream Recordings</span>
            </div>
            <div className="event-card-recordings-list">
              {event.streamRecordings.map((recording) => (
                <a
                  key={recording.id}
                  href={`/stream/recording/${recording.id}`}
                  className="event-card-recording-item"
                >
                  <span className="recording-title">{recording.title}</span>
                  <span className="recording-duration">
                    {Math.floor(recording.duration / 60)}:{(recording.duration % 60).toString().padStart(2, '0')}
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="event-card-actions">
          <Link to={`/events/${event.id}`} className="event-card-link">
            View Event Details
          </Link>
          {event.status === 'upcoming' && (
            <Link to={`/events/${event.id}`} className="event-card-rsvp-link">
              RSVP Now
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventCard;


